import React from "react";
import { DohopOffer, DohopDataLists } from "../../types/dohop";
import { formatDuration, formatTime } from "../../services/dohopApi";

interface DohopFlightCardProps {
    offer: DohopOffer;
    dataLists: DohopDataLists;
    onSelect: (offer: DohopOffer) => void;
}

const DohopFlightCard: React.FC<DohopFlightCardProps> = ({
    offer,
    dataLists,
    onSelect,
}) => {
    // Get journey IDs from offer items
    const journeyIds = offer.offer_items[0]?.services[0]?.journey_ids || [];

    // Get journey and its segments
    const journey = dataLists.journeys.find((j) =>
        journeyIds.includes(j.journey_id)
    );

    const segments = journey
        ? journey.segment_ids
              .map((segId) =>
                  dataLists.segments.find((s) => s.segment_id === segId)
              )
              .filter(Boolean)
        : [];

    if (segments.length === 0) {
        return null;
    }

    const firstSegment = segments[0]!;
    const lastSegment = segments[segments.length - 1]!;

    // Calculate total duration
    const totalDuration = segments.reduce(
        (acc, seg) => acc + (seg?.duration || 0),
        0
    );

    // Get stops count
    const stopsCount = segments.length - 1;
    const stopsText =
        stopsCount === 0
            ? "Direct"
            : `${stopsCount} stop${stopsCount > 1 ? "s" : ""}`;

    // Get carrier info
    const carrierCode = firstSegment.marketing_carrier.carrier_code;
    const flightNumber = firstSegment.marketing_carrier.flight_number;

    return (
        <div className="bg-white rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Flight info */}
                    <div className="flex-1">
                        <div className="flex items-center gap-4">
                            {/* Airline logo placeholder */}
                            <div className="w-12 h-12 bg-dohop-ice rounded-lg flex items-center justify-center">
                                <span className="text-dohop-blue font-bold text-sm">
                                    {carrierCode}
                                </span>
                            </div>

                            {/* Times and route */}
                            <div className="flex-1">
                                <div className="flex items-center gap-2 md:gap-4">
                                    {/* Departure */}
                                    <div className="text-center">
                                        <div className="text-xl font-bold text-dohop-navy">
                                            {formatTime(
                                                firstSegment.departure.datetime
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {
                                                firstSegment.departure
                                                    .airport_code
                                            }
                                        </div>
                                    </div>

                                    {/* Flight path visualization */}
                                    <div className="flex-1 px-2">
                                        <div className="relative">
                                            <div className="border-t-2 border-dashed border-gray-300 w-full"></div>
                                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-2">
                                                <svg
                                                    className="w-5 h-5 text-dohop-blue"
                                                    fill="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div className="text-center mt-1">
                                            <span className="text-xs text-gray-500">
                                                {formatDuration(totalDuration)}
                                            </span>
                                            <span className="mx-1 text-gray-300">
                                                |
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {stopsText}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Arrival */}
                                    <div className="text-center">
                                        <div className="text-xl font-bold text-dohop-navy">
                                            {formatTime(
                                                lastSegment.arrival.datetime
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {lastSegment.arrival.airport_code}
                                        </div>
                                    </div>
                                </div>

                                {/* Flight details */}
                                <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                                    <span>
                                        {carrierCode}
                                        {flightNumber}
                                    </span>
                                    {firstSegment.cabin_type && (
                                        <>
                                            <span className="text-gray-300">
                                                |
                                            </span>
                                            <span className="capitalize">
                                                {firstSegment.cabin_type}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Price and select */}
                    <div className="flex items-center gap-4 md:flex-col md:items-end">
                        <div className="text-right">
                            <div className="text-2xl font-bold text-dohop-blue">
                                {offer.total_price.currency}{" "}
                                {offer.total_price.amount.toFixed(2)}
                            </div>
                            <div className="text-sm text-gray-500">
                                per person
                            </div>
                        </div>
                        <button
                            onClick={() => onSelect(offer)}
                            className="px-6 py-2 bg-dohop-blue text-white rounded-lg font-semibold hover:bg-dohop-blue-dark transition-colors"
                        >
                            Select
                        </button>
                    </div>
                </div>
            </div>

            {/* Dohop protection badge */}
            <div className="border-t border-gray-100 px-4 md:px-6 py-2 bg-dohop-ice/50">
                <div className="flex items-center gap-2 text-sm text-dohop-blue">
                    <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fillRule="evenodd"
                            d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                        />
                    </svg>
                    <span className="font-medium">Dohop Protection</span>
                    <span className="text-gray-500">
                        - Guaranteed connection
                    </span>
                </div>
            </div>
        </div>
    );
};

export default DohopFlightCard;
